import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Check, ArrowRight, Clock, Sparkles, Users } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

const STEPS = [
  {
    key: "color",
    title: "Milyen alapszínen szeretnél dolgozni?",
    options: [
      { v: "fekete", label: "Fekete" },
      { v: "barna", label: "Sötét barna" },
      { v: "gesztenye", label: "Gesztenye / mokka" },
      { v: "szoke", label: "Szőke" },
      { v: "hideg", label: "Hideg / platina szőke" },
    ],
  },
  {
    key: "length",
    title: "Milyen hosszt szeretnél elérni?",
    options: [
      { v: "55", label: "55 cm (váll alá)" },
      { v: "60", label: "60 cm (mellközép)" },
      { v: "65", label: "65 cm (mell alá)" },
      { v: "68", label: "68 cm vagy hosszabb" },
    ],
  },
  {
    key: "thickness",
    title: "Milyen vastag a saját hajad?",
    options: [
      { v: "vekony", label: "Vékony, finomszálú" },
      { v: "kozepes", label: "Közepes vastagságú" },
      { v: "vastag", label: "Vastag, sűrű" },
    ],
  },
  {
    key: "install",
    title: "Mi a célod a hosszabbítással?",
    options: [
      { v: "hosszabbitas", label: "Csak hosszabb haj" },
      { v: "dusitas", label: "Inkább dúsítás" },
      { v: "mindketto", label: "Hossz és dúsítás" },
    ],
  },
  {
    key: "origin",
    title: "Mennyit tudsz most rászánni?",
    options: [
      { v: "europai", label: "100 000–250 000 Ft" },
      { v: "kazah", label: "200 000–400 000 Ft" },
      { v: "nem-tudom", label: "Még nem tudom, mutasd mind" },
    ],
  },
];

const COLOR_SWATCHES = [
  { name: "Fekete", hex: "#1a1614" },
  { name: "Sötét barna", hex: "#3d2820" },
  { name: "Gesztenye", hex: "#5b3a25" },
  { name: "Szőke", hex: "#a47b4b" },
  { name: "Platina szőke", hex: "#c9b289" },
];

export default function Quiz({ embedded = false }) {
  const Heading = embedded ? "h2" : "h1";
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  useRevealAll(ref);

  const total = STEPS.length;
  const current = STEPS[step];

  const select = (val) => {
    const next = { ...answers, [current.key]: val };
    setAnswers(next);
    setTimeout(() => {
      if (step === total - 1) setDone(true);
      else setStep(step + 1);
    }, 300);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  return (
    <section
      ref={ref}
      data-testid="quiz-section"
      className={`${embedded ? "py-28 md:py-40" : "pt-32 pb-32 md:pt-40 md:pb-40"} bg-[color:var(--he-cream)] relative overflow-hidden`}
    >
      {/* Decorative figure */}
      <div className="absolute top-12 left-0 hidden md:block opacity-[0.04] pointer-events-none select-none">
        <span className="figure italic text-[26rem] leading-none text-[color:var(--he-ink)]">
          06
        </span>
      </div>

      <div className="he-container relative">
        {/* Heading */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20 items-end">
          <div className="md:col-span-5 reveal">
            <p className="overline mb-6 mag-marker">
              <span>Hajtípus választó</span>
            </p>
            <Heading
              className="font-serif-display leading-[0.98] tracking-[-0.022em]"
              style={{ fontWeight: 400, fontSize: "clamp(2.4rem, 5.6vw, 5.2rem)" }}
            >
              Melyik tincs <br />
              <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                illik hozzád?
              </span>
            </Heading>
          </div>
          <div className="md:col-span-4 md:col-start-8 reveal delay-1">
            <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.7] text-[15px]">
              Öt rövid kérdés a hajadról és a kívánt eredményről. A végén
              megmutatjuk, melyik termék illik hozzád a 11-ből.
            </p>
            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-[color:var(--he-gold)]" />
                <span className="overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.62rem" }}>
                  60 mp
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[color:var(--he-gold)]" />
                <span className="overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.62rem" }}>
                  5 lépés
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={14} className="text-[color:var(--he-gold)]" />
                <span className="overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.62rem" }}>
                  Diszkrét
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column: quiz + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Quiz card — main */}
          <div className="lg:col-span-8 reveal">
            {!done ? (
              <div className="bg-[color:var(--he-bg-alt)] border border-[color:var(--he-line)] p-7 md:p-12">
                {/* Step header */}
                <div className="flex items-center justify-between mb-10 pb-6 border-b border-[color:var(--he-line)]">
                  <div className="flex items-baseline gap-3">
                    <span className="figure italic text-[color:var(--he-gold)] text-3xl">
                      {String(step + 1).padStart(2, "0")}
                    </span>
                    <span className="overline-xs text-[color:var(--he-ink-soft)]">
                      / {String(total).padStart(2, "0")}
                    </span>
                    <span className="ml-3 overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline" style={{ fontSize: "0.6rem" }}>
                      · {current.key}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {STEPS.map((s, i) => (
                      <span
                        key={s.key}
                        className={`block h-px transition-all duration-500 ${
                          i < step
                            ? "w-5 bg-[color:var(--he-gold)]"
                            : i === step
                              ? "w-9 bg-[color:var(--he-ink)]"
                              : "w-5 bg-[color:var(--he-line)]"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Question */}
                <p
                  className="font-serif-display text-[1.7rem] md:text-[2.4rem] leading-[1.1] mb-9 max-w-2xl"
                  data-testid={`quiz-question-${step}`}
                  style={{ fontWeight: 400 }}
                >
                  {current.title}
                </p>

                {/* Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {current.options.map((o) => (
                    <button
                      key={o.v}
                      onClick={() => select(o.v)}
                      data-testid={`quiz-option-${current.key}-${o.v}`}
                      className={`quiz-option font-sans-geo text-sm tracking-wide ${
                        answers[current.key] === o.v ? "selected" : ""
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                <div className="mt-10 flex items-center justify-between">
                  <button
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                    data-testid="quiz-prev"
                    className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed text-[color:var(--he-ink)]"
                  >
                    <span>← Előző</span>
                  </button>
                  <button
                    onClick={() => answers[current.key] && (step === total - 1 ? setDone(true) : setStep(step + 1))}
                    disabled={!answers[current.key]}
                    data-testid="quiz-next"
                    className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed text-[color:var(--he-ink)]"
                  >
                    <span>Tovább</span>
                    <ChevronRight size={14} className="ghost-arrow" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                data-testid="quiz-result"
                className="bg-[color:var(--he-bg-alt)] border border-[color:var(--he-gold)]/40 p-10 md:p-16 text-center relative he-rise"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[color:var(--he-gold)] mx-auto">
                  <Check className="text-[color:var(--he-gold)]" size={20} strokeWidth={1.5} />
                </div>
                <p className="overline mt-8 mb-5">Az ajánlásunk</p>
                <h3
                  className="font-serif-display leading-[1.05] tracking-tight"
                  style={{ fontWeight: 400, fontSize: "clamp(2rem, 4.4vw, 4rem)" }}
                >
                  {answers.origin === "kazah"
                    ? "Kazahsztáni"
                    : answers.origin === "europai"
                      ? "Európai"
                      : "Európai vagy kazahsztáni"}{" "}
                  <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                    {answers.thickness === "vastag"
                      ? "vastagabb szállal"
                      : answers.thickness === "vekony"
                        ? "finom szállal"
                        : "közepes szállal"}
                  </span>
                </h3>
                <p className="mt-7 text-[color:var(--he-ink-soft)] max-w-xl mx-auto font-light leading-relaxed">
                  {answers.length || "55"} cm hossz · {answers.color === "fekete" ? "fekete" : answers.color === "szoke" ? "szőke" : answers.color === "hideg" ? "platina szőke" : answers.color === "gesztenye" ? "gesztenye" : "barna"} alaptónus
                  {answers.install === "dusitas" ? " · dúsításhoz" : answers.install === "mindketto" ? " · hosszhoz és dúsításhoz" : " · hosszabbításhoz"}.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
                  <Link
                    to={`/termekek?o=${answers.origin === "kazah" ? "kazah" : answers.origin === "europai" ? "europai" : "all"}`}
                    data-testid="quiz-cta-results"
                    className="btn-arrow text-[color:var(--he-ink)]"
                  >
                    <span className="arrow-circle">
                      <ArrowRight size={18} className="arrow-icon" />
                    </span>
                    <span style={{ fontWeight: 500 }}>Ajánlott termékek</span>
                  </Link>
                  <button onClick={reset} data-testid="quiz-restart" className="btn-ghost text-[color:var(--he-ink-soft)]">
                    <span>Újrakezdem</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-32 reveal delay-2">
            {/* Image card with caption */}
            <div className="he-img-wrap aspect-[4/5] mb-8">
              <img
                src="https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Hosszú haj — természetes, kutikula-irányított"
                className="he-img w-full h-full object-cover"
              />
              <div className="absolute top-5 left-5 bg-[color:var(--he-gold)] text-[color:var(--he-ink)] px-3 py-1.5">
                <span className="overline-xs" style={{ fontSize: "0.55rem", letterSpacing: "0.28em" }}>
                  Tipp
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p
                  className="font-serif-display italic text-xl leading-tight"
                  style={{ fontWeight: 400 }}
                >
                  „Inkább kevesebbet, <br /> de jót."
                </p>
              </div>
            </div>

            {/* Mit kap benefits */}
            <div className="bg-[color:var(--he-bg-alt)] border border-[color:var(--he-line)] p-6">
              <p className="overline-xs text-[color:var(--he-gold)] mb-5">Amit kapsz</p>
              <ul className="space-y-4">
                <Benefit num="01" text="Konkrét termékajánlás a 11-ből, képpel és árral" />
                <Benefit num="02" text="Javasolt hossz és súly a saját hajadhoz mérve" />
                <Benefit num="03" text="Ha kérdésed van, válaszolunk 24 órán belül" />
              </ul>
            </div>

            {/* Color swatches */}
            <div className="mt-6 bg-[color:var(--he-bg-alt)] border border-[color:var(--he-line)] p-6">
              <p className="overline-xs text-[color:var(--he-gold)] mb-5">
                Elérhető tónusok
              </p>
              <div className="flex items-center gap-2.5 mb-4">
                {COLOR_SWATCHES.map((c) => (
                  <div
                    key={c.hex}
                    title={c.name}
                    className="w-9 h-9 rounded-full border border-[color:var(--he-line)] hover:scale-110 transition-transform duration-300 cursor-pointer"
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
              <p className="text-xs text-[color:var(--he-ink-soft)] font-light leading-relaxed">
                Fekete · sötét barna · gesztenye · szőke · platina szőke. A
                pontos árnyalat a termékadatlapon szerepel.
              </p>
            </div>
          </aside>
        </div>

        {/* Bottom strip — small editorial trust */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-[color:var(--he-line)] pt-12">
          <TrustItem
            num="01"
            title="Személyes válasz"
            text="Ha a kérdőív után még kérdésed van, írj nekünk — 24 órán belül válaszolunk."
          />
          <TrustItem
            num="02"
            title="Nincs regisztráció"
            text="A választó nem kér adatot. Csak az eredménnyel találkozol, semmit nem mentünk."
          />
          <TrustItem
            num="03"
            title="Újra is kitöltheted"
            text="Ha mégis másra gondoltál, egy kattintással újraindíthatod a kérdéseket."
          />
        </div>
      </div>
    </section>
  );
}

function Benefit({ num, text }) {
  return (
    <li className="flex items-baseline gap-4">
      <span className="figure italic text-[color:var(--he-gold)] text-xl shrink-0 w-6">
        {num}
      </span>
      <span className="text-sm text-[color:var(--he-ink)] font-light leading-relaxed">
        {text}
      </span>
    </li>
  );
}

function TrustItem({ num, title, text }) {
  return (
    <div className="reveal">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="figure italic text-[color:var(--he-gold)] text-2xl">{num}</span>
        <p
          className="font-serif-display text-xl"
          style={{ fontWeight: 400 }}
        >
          {title}
        </p>
      </div>
      <p className="text-sm text-[color:var(--he-ink-soft)] font-light leading-[1.7] max-w-xs">
        {text}
      </p>
    </div>
  );
}
